import { Component, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import {
  CardBodyComponent,
  CardComponent,
  ColComponent,
  RowComponent,
  TableDirective
} from '@coreui/angular';
import { CommonModule } from '@angular/common';
import { SpinnerComponent } from '@coreui/angular';
import { DtrService } from '../../services/dtr.service';

@Component({
  selector: 'app-attendance',
  templateUrl: './attendance.component.html',
  styleUrls: ['./attendance.component.scss'],
    imports: [CommonModule, CardComponent, CardBodyComponent, RowComponent, ColComponent, ReactiveFormsModule, TableDirective, SpinnerComponent, FormsModule]
})
export class AttendanceComponent implements OnInit {
  clockedInTotalItems: number = 0;
  absentTotalItems: number = 0;
  constructor(private dtrService: DtrService) { }
  public timeZone = 'America/New_York';
  public data: any[] = [];

  currentTime = this.getCurrentDateInTimeZone(this.timeZone);
  loading = false;

  activeTab: 'clockedIn' | 'absent' = 'clockedIn';
  clockedInData: any[] = [];
  absentData: any[] = [];
  absentPage: number = 1;
  absentPerPage: number = 10;
  absentTotal: number = 0;
  absentTotalPages: number = 1;
  absentLoadedDate: string | null = null;

  ngOnInit(): void {
    this.getDtrToday(this.currentTime);
  }

  // Pagination for Clocked In

  onTabChange(tab: 'clockedIn' | 'absent') {
    this.activeTab = tab;
    if (tab === 'absent') {
      this.loadAbsentEmployees(1);
    }
  }

  loadAbsentEmployees(page: number) {
    this.absentPage = page;
    this.loading = true;
    this.dtrService.getAbsentEmployeesByDatePaginated(this.currentTime, this.absentPerPage, this.absentPage).subscribe({
      next: (response: any) => {
        this.absentData = response?.absent_employees || [];
        this.absentTotal = response?.pagination?.total || 0;
        this.absentTotalItems = this.absentTotal;
        this.absentTotalPages = response?.pagination?.last_page || 1;
        this.absentLoadedDate = this.currentTime;
        this.loading = false;
      },
      error: (error: any) => {
        this.loading = false;
      }
    });
  }

  onAbsentPageChange(page: number) {
    if (!this.loading && page >= 1 && page <= this.absentTotalPages && page !== this.absentPage) {
      this.loadAbsentEmployees(page);
    }
  }

  getDtrToday(dateNow: any) {
    this.loading = true;
    this.dtrService.getOnlineEmployeesByDate(dateNow, this.clockedInPerPage, this.clockedInPage).subscribe({
      next: (response: any) => {
        this.data = response?.data || [];
        // Set clockedInPage from backend pagination
        const backendPage = Number(response?.pagination?.current_page) || 1;
        this.clockedInPage = backendPage;
        this.loadClockedInEmployees(this.clockedInPage);
        this.filterTabs();
        this.loading = false;
      },
      error: (error: any) => {
        this.loading = false;
      }
    });
  }

  filterTabs() {
    // Absent: no time_in and no time_out
    this.absentData = (this.data || []).filter(rec => !rec.time_in && !rec.time_out);
  }

  getCurrentDateInTimeZone(timeZone: string): string {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    return formatter.format(now);
  }

  onDateChange(event: Event) {
    const input = event.target as HTMLInputElement;
    this.currentTime = input.value;
    this.getDtrToday(this.currentTime);
    // Reset absent tab data for new date
    this.absentLoadedDate = null;
    if (this.activeTab === 'absent') {
      this.onTabChange('absent');
    }
    // Reset clocked in pagination
    this.clockedInPage = 1;
  }

    // ...existing properties...

  // Pagination for Clocked In (copied from employee component)
  clockedInPage: number = 1;
  clockedInPerPage: number = 10;
  clockedInTotal: number = 0;
  clockedInTotalPages: number = 1;
  visibleClockedInPageChunk = 0;

  loadClockedInEmployees(page: number) {
    this.loading = true;
    // Always use the requested page unless out of bounds
    const allClockedIn = (this.data || []).filter(rec => rec.time_in || rec.time_out);
    this.clockedInTotal = allClockedIn.length;
    this.clockedInTotalItems = this.clockedInTotal;
    this.clockedInTotalPages = Math.max(1, Math.ceil(this.clockedInTotal / this.clockedInPerPage));
    let newPage = page;
    if (newPage < 1) newPage = 1;
    if (newPage > this.clockedInTotalPages) newPage = this.clockedInTotalPages;
    this.clockedInPage = newPage;
    const start = (this.clockedInPage - 1) * this.clockedInPerPage;
    const end = start + this.clockedInPerPage;
    this.clockedInData = allClockedIn.slice(start, end);
    this.loading = false;
  }
  onClockedInPageChange(page: number) {
    this.clockedInPage = page;
    this.loadClockedInEmployees(page);
  }

  onClockedInPerPageChange() {
    this.clockedInPage = 1;
    this.loadClockedInEmployees(this.clockedInPage);
  }

  getClockedInChunkedPagination(): (number | 'ellipsis')[] {
    const chunkSize = 5;
    const totalChunks = Math.ceil(this.clockedInTotalPages / chunkSize);
    const currentChunk = Math.floor((this.clockedInPage - 1) / chunkSize);
    this.visibleClockedInPageChunk = currentChunk;
    const start = currentChunk * chunkSize + 1;
    const end = Math.min(start + chunkSize - 1, this.clockedInTotalPages);
    const pages: (number | 'ellipsis')[] = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    if (end < this.clockedInTotalPages) {
      pages.push('ellipsis');
    }
    if (start > 1) {
      pages.unshift('ellipsis');
    }
    return pages;
  }

  onClockedInChunkEllipsisClick(next: boolean) {
    const chunkSize = 5;
    if (next) {
      const nextPage = (this.visibleClockedInPageChunk + 1) * chunkSize + 1;
      this.onClockedInPageChange(Math.min(nextPage, this.clockedInTotalPages));
    } else {
      const prevPage = (this.visibleClockedInPageChunk - 1) * chunkSize + 1;
      this.onClockedInPageChange(Math.max(prevPage, 1));
    }
  }
}